/**
 * One row of „Pytania własne": the grip, the question, its type pill, the bookmark and the trash.
 *
 * ── Three ways to move it, and they are the same operation ───────────────────────────────────
 * The pointer drags the grip; the keyboard lifts it with Space and steers with the arrows
 * (`shared/ui/dnd/useKeyboardMove`); both commit through `onMove`, which is the reducer's `move`.
 * There is no reduced fallback and no second code path.
 *
 * A separate ⋯ „Przestaw" entry is deliberately NOT added. `shared/ui/dnd/useMenuLift` exists for
 * surfaces that draw no grip at all (owner, 15 September 2026); the canon draws a grip here, and a
 * menu duplicating what the grip already does would be a second control for one act sitting a
 * centimetre from the first.
 *
 * ── The bookmark is a STATE, not a switch ────────────────────────────────────────────────────
 * Filled means the question is already on the trainer's shelf, which is exactly `sourceQuestionId`
 * being set — which is why the press has to write the minted id back into the draft, or the second
 * press files the same question a second time. It is refused once filled rather than hidden: a
 * control that vanished after one press would leave the trainer unsure whether it had worked. R35's
 * shelf is an addition, never a toggle — removing a question from the shelf is the catalog screen's
 * own trash, and the two acts are not the same act.
 *
 * ── A refused bookmark says WHY, in the accessibility tree ───────────────────────────────────
 * `aria-disabled` with the reason as a rendered element `aria-describedby` points at, the shape
 * `DragHandle` settled: a `title` reaches neither a touch user nor a keyboard user who never hovers.
 */

import { useId, useRef } from 'react';

import { DragHandle } from '~/shared/ui/dnd/DragHandle';
import { useDraggableItem } from '~/shared/ui/dnd/useDraggableItem';
import { useDropTarget } from '~/shared/ui/dnd/useDropTarget';
import { useKeyboardMove } from '~/shared/ui/dnd/useKeyboardMove';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { CustomDraftItem } from '../model/editorDraft';
import { QUESTION_DRAG_KIND } from '../lib/useQuestionMove';
import type { TranslateKey } from '../model/translateKey';
import { AnswerTypePill } from './AnswerTypePill';
import { RequiredMark } from '~/shared/ui/RequiredMark';
import { BookmarkIcon, TrashIcon } from '~/shared/ui/ReportIcons';
import canon from '~/shared/ui/surveyCanon.module.css';
import styles from './EditorForm.module.css';

export interface CustomQuestionRowProps {
  readonly item: CustomDraftItem;
  readonly index: number;
  readonly count: number;
  /** Resolved by the form, which reads the names in one batch. `null` while the batch is open. */
  readonly exerciseName: string | null;
  /** The element carrying the keyboard instruction, rendered once for the whole section. */
  readonly hintId: string;
  readonly onMove: (id: string, targetIndex: number, label: string) => void;
  /** A keyboard PICKUP: it names what was picked up and how the arrows now behave. */
  readonly onLift: (position: number) => void;
  readonly onPreview: (position: number) => void;
  readonly onCancelLift: () => void;
  readonly onRemove: (id: string) => void;
  readonly onBookmark: (item: CustomDraftItem) => void;
  readonly bookmarkBusy: boolean;
  readonly t: TranslateKey;
}

export function CustomQuestionRow({
  item,
  index,
  count,
  exerciseName,
  hintId,
  onMove,
  onLift,
  onPreview,
  onCancelLift,
  onRemove,
  onBookmark,
  bookmarkBusy,
  t,
}: CustomQuestionRowProps) {
  const rowRef = useRef<HTMLLIElement>(null);
  const gripRef = useRef<HTMLButtonElement>(null);
  const bookmarkReasonId = useId();

  const canMove = count > 1;

  const drag = useDraggableItem({
    ref: rowRef,
    handleRef: gripRef,
    // The index travels WITH the drag: the drop resolves a gap, and a gap below the question's own
    // place shifts up by one the moment the move lifts it out.
    data: { kind: QUESTION_DRAG_KIND, id: item.id, label: item.label, index },
    canDrag: canMove,
  });

  const drop = useDropTarget({
    ref: rowRef,
    data: { kind: QUESTION_DRAG_KIND, index },
    canDrop: (source) => canMove && source.kind === QUESTION_DRAG_KIND,
    allowedEdges: ['top', 'bottom'],
  });

  const keyboard = useKeyboardMove({
    count,
    index,
    axis: 'vertical',
    canLift: canMove,
    onLift,
    onPreview,
    onCommit: (target) => {
      onMove(item.id, target, item.label);
    },
    onCancel: onCancelLift,
  });

  // A shelf copy cannot be an exercise recording — `CustomQuestionRules` refuses to save one,
  // because the type names one specific movement — so the bookmark states that rather than failing.
  const bookmarkRefusal =
    item.answerType === 'ExerciseClip'
      ? t('web:reports.editor.question.bookmarkClip')
      : item.sourceQuestionId !== null
        ? t('web:reports.editor.question.bookmarkDone')
        : null;

  return (
    <li
      ref={rowRef}
      className={styles.row}
      data-edge={drop.isOver ? (drop.edge ?? undefined) : undefined}
      data-dragging={drag.isDragging ? '' : undefined}
    >
      <DragHandle
        ref={gripRef}
        className={styles.grip}
        wide
        label={t('web:reports.editor.question.grip', { label: item.label })}
        describedBy={hintId}
        disabledReason={canMove ? null : t('web:reports.editor.question.gripAlone')}
        isLifted={keyboard.isLifted}
        onKeyDown={keyboard.onKeyDown}
        onBlur={keyboard.onBlur}
      />

      <span className={styles.rowText}>
        <span className={styles.rowLabel}>
          <span className={styles.rowLabelText}>{item.label}</span>
          {item.required ? <RequiredMark label={t('web:reports.editor.newQuestion.required')} /> : null}
        </span>
        <span className={styles.rowMeta}>
          <AnswerTypePill
            answerType={item.answerType}
            optionCount={item.options.length}
            exerciseName={exerciseName}
            t={t}
          />
        </span>
      </span>

      <button
        type="button"
        className={`${String(canon.ibtn)} ${String(canon.ibtnBare)} ${
          item.sourceQuestionId === null ? '' : String(canon.ibtnOn)
        }`}
        aria-label={
          item.sourceQuestionId === null
            ? t('web:reports.editor.question.bookmark')
            : t('web:reports.editor.question.bookmarkDone')
        }
        title={bookmarkRefusal ?? t('web:reports.editor.question.bookmark')}
        // `aria-disabled`, never `disabled`: the control keeps its place in the tab order and takes
        // its explanation with it. The no-op below is what makes the advisory attribute real.
        aria-disabled={bookmarkRefusal !== null || bookmarkBusy ? true : undefined}
        aria-describedby={bookmarkRefusal === null ? undefined : bookmarkReasonId}
        onClick={() => {
          if (bookmarkRefusal !== null || bookmarkBusy) return;
          onBookmark(item);
        }}
      >
        <BookmarkIcon className={canon.ibtnIconMd} filled={item.sourceQuestionId !== null} />
        {/* Rendered, never drawn: a 28px icon button has nowhere to put a sentence, and the filled
          * or dimmed mark is the visual channel this is the assistive counterpart of. */}
        {bookmarkRefusal === null ? null : (
          <span id={bookmarkReasonId} className={hidden.srOnly}>
            {bookmarkRefusal}
          </span>
        )}
      </button>

      <button
        type="button"
        className={`${String(canon.ibtn)} ${String(canon.ibtnBare)} ${String(canon.ibtnDanger)}`}
        aria-label={t('web:reports.editor.question.remove', { label: item.label })}
        onClick={() => {
          onRemove(item.id);
        }}
      >
        <TrashIcon className={canon.ibtnIconMd} />
      </button>
    </li>
  );
}
